import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { CurrencyCode, CurrencyInfo } from '../types/Billing';

const STORAGE_KEY = 'selectedCurrency';

// Default currencies (used before API fetch completes)
const DEFAULT_CURRENCIES: CurrencyInfo[] = [
  { code: 'EUR', name: 'Euro', symbol: '€', isBase: true },
  { code: 'XOF', name: 'CFA Franc', symbol: 'F', isBase: false },
  { code: 'USD', name: 'US Dollar', symbol: '$', isBase: false },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', isBase: false },
];

// Geo-detection: Map timezone regions to default currencies
const detectCurrencyFromTimezone = (): CurrencyCode => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Africa (primarily West Africa) -> XOF
    if (
      timezone.startsWith('Africa/Dakar') ||
      timezone.startsWith('Africa/Abidjan') ||
      timezone.startsWith('Africa/Bamako') ||
      timezone.startsWith('Africa/Conakry') ||
      timezone.startsWith('Africa/Ouagadougou') ||
      timezone.startsWith('Africa/Niamey') ||
      timezone.startsWith('Africa/Lome') ||
      timezone.startsWith('Africa/Cotonou') ||
      timezone.startsWith('Africa/')
    ) {
      return 'XOF';
    }

    // Middle East -> AED
    if (
      timezone.startsWith('Asia/Dubai') ||
      timezone.startsWith('Asia/Abu_Dhabi') ||
      timezone.startsWith('Asia/Riyadh') ||
      timezone.startsWith('Asia/Qatar') ||
      timezone.startsWith('Asia/Bahrain') ||
      timezone.startsWith('Asia/Kuwait')
    ) {
      return 'AED';
    }

    // Americas -> USD
    if (
      timezone.startsWith('America/') ||
      timezone.startsWith('US/') ||
      timezone.startsWith('Canada/')
    ) {
      return 'USD';
    }

    // Europe -> EUR
    if (timezone.startsWith('Europe/') || timezone.startsWith('Atlantic/')) {
      return 'EUR';
    }

    // Default to USD for unknown regions
    return 'USD';
  } catch {
    return 'USD';
  }
};

// Get initial currency from localStorage or geo-detect
const getInitialCurrency = (): CurrencyCode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && ['XOF', 'EUR', 'USD', 'AED'].includes(stored)) {
    return stored as CurrencyCode;
  }
  return detectCurrencyFromTimezone();
};

type CurrencyContextType = {
  currency: CurrencyCode;
  currencies: CurrencyInfo[];
  setCurrency: (code: CurrencyCode) => void;
  getCurrencySymbol: (code?: CurrencyCode) => string;
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrencyState] =
    useState<CurrencyCode>(getInitialCurrency);
  const [currencies, setCurrencies] =
    useState<CurrencyInfo[]>(DEFAULT_CURRENCIES);

  // Fetch currencies from API
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const response = await fetch(`${API_URL}/plans/currencies`);
        if (response.ok) {
          const data = await response.json();
          setCurrencies(data);
        }
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
      }
    };
    fetchCurrencies();
  }, []);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    localStorage.setItem(STORAGE_KEY, code);
  }, []);

  const getCurrencySymbol = useCallback(
    (code?: CurrencyCode) => {
      const targetCode = code || currency;
      const found = currencies.find(c => c.code === targetCode);
      return found?.symbol || targetCode;
    },
    [currency, currencies]
  );

  return (
    <CurrencyContext.Provider
      value={{ currency, currencies, setCurrency, getCurrencySymbol }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
